import { Button } from '@react-spectrum/s2';
import { memo, useCallback, useState } from 'react';

import { useBridgeApi } from '@/api/BridgeApi.ts';
import { Section } from '@/layouts/settings/Section';
import { trackEvent } from '@/utils/fullstory';

import {
	MemoryImportPasteForm,
	stepNumberStyle,
} from '../onboarding/screens/memoryImport/MemoryImportPasteForm';
import { ProviderPickList } from '../onboarding/screens/memoryImport/ProviderPickList';
import { type Provider } from '../onboarding/screens/memoryImport/providers';

type Step = 'collapsed' | 'pick' | 'paste' | 'done';

const STEP_NUMBER: Record<Exclude<Step, 'collapsed'>, number> = {
	pick: 1,
	paste: 2,
	done: 3,
};

// Matches the underlined muted links inside the shared import flow, so the
// card's Cancel / restart affordances read as part of the same UI.
const linkButtonStyle: React.CSSProperties = {
	background: 'none',
	border: 'none',
	padding: 0,
	cursor: 'pointer',
	color: 'var(--text-muted)',
	fontSize: 14,
	textDecoration: 'underline',
};

const stepHeadingStyle: React.CSSProperties = {
	fontSize: 18,
	fontWeight: 600,
	color: 'var(--text-primary)',
};

export const MemoriesSettings = memo(function MemoriesSettings() {
	const bridgeApi = useBridgeApi();
	const websiteUrl = bridgeApi.user_state?.onboarding_status?.website_url;
	const instagramHandle =
		bridgeApi.user_state?.onboarding_status?.instagram_handle;
	const memoryImport = bridgeApi.user_state?.onboarding_status?.memory_import;

	const [step, setStep] = useState<Step>('collapsed');
	const [provider, setProvider] = useState<Provider | null>(null);
	// The report already sitting in bridge state when the user hit submit. The
	// reducer clears it to null on a round-trip, but a stale report from an
	// earlier import can still be there — only a *different object* means the
	// ingestion result for THIS submit has arrived.
	const [reportAtSubmit, setReportAtSubmit] = useState<
		typeof memoryImport | null
	>(null);

	const handleOpen = useCallback(() => {
		trackEvent('Memory Import Opened');
		setStep('pick');
	}, []);

	const handleCancel = useCallback(() => {
		trackEvent('Memory Import Cancelled', { step });
		setStep('collapsed');
		setProvider(null);
		setReportAtSubmit(null);
	}, [step]);

	const handlePick = useCallback((picked: Provider) => {
		trackEvent('Memory Import Source Picked', { provider: picked });
		setProvider(picked);
		setStep('paste');
	}, []);

	const handleSubmit = useCallback(
		(snippet: string) => {
			trackEvent('Memory Import Submitted', { provider });
			setReportAtSubmit(
				bridgeApi.user_state?.onboarding_status?.memory_import ?? null,
			);
			bridgeApi.sendAction({ type: 'request_import_ai_memory', snippet });
			setStep('done');
		},
		[bridgeApi, provider],
	);

	const handleRestart = useCallback(() => {
		trackEvent('Memory Import Restarted');
		setProvider(null);
		setStep('pick');
	}, []);

	const handleDone = useCallback(() => {
		trackEvent('Memory Import Done');
		setStep('collapsed');
		setProvider(null);
		setReportAtSubmit(null);
	}, []);

	// Ingestion finishes minutes after the fire-and-forget submit, so a failed
	// report can land while the done step is showing.
	const showImportFailure =
		step === 'done' &&
		!!memoryImport &&
		memoryImport !== reportAtSubmit &&
		(memoryImport.status === 'failed' || memoryImport.status === 'partial');

	return (
		<Section title="Memory">
			{step === 'collapsed' ? (
				<div className="flex items-center gap-3">
					<p
						className="flex-1"
						style={{
							fontSize: 14,
							lineHeight: 1.5,
							color: 'var(--text-primary)',
						}}
					>
						Worked with another AI on your business? Bring that context into
						Bridgeway so it picks up where you left off.
					</p>
					<Button variant="primary" onPress={handleOpen}>
						Import
					</Button>
				</div>
			) : (
				<div className="flex flex-col gap-4">
					<div className="flex items-center" style={{ gap: 10 }}>
						{(['pick', 'paste', 'done'] as const).map((s) => (
							<div
								key={s}
								aria-current={s === step ? 'step' : undefined}
								style={
									s === step
										? stepNumberStyle
										: { ...stepNumberStyle, background: 'rgba(0,0,0,0.25)' }
								}
							>
								{STEP_NUMBER[s]}
							</div>
						))}
					</div>

					{step === 'pick' && (
						<div>
							<h4 className="mb-4" style={stepHeadingStyle}>
								Where does your work live?
							</h4>
							<ProviderPickList
								websiteUrl={websiteUrl}
								instagramHandle={instagramHandle}
								onPick={handlePick}
							/>
							<button
								type="button"
								onClick={handleCancel}
								style={linkButtonStyle}
							>
								Cancel
							</button>
						</div>
					)}

					{step === 'paste' && provider && (
						<div>
							<MemoryImportPasteForm
								provider={provider}
								websiteUrl={websiteUrl}
								instagramHandle={instagramHandle}
								onSubmit={handleSubmit}
								secondaryAction={
									<button
										type="button"
										onClick={handleCancel}
										style={linkButtonStyle}
									>
										Cancel
									</button>
								}
							/>
						</div>
					)}

					{step === 'done' && (
						<div className="flex flex-col gap-4">
							<h4 style={stepHeadingStyle}>There — now I actually know you.</h4>
							<p
								style={{
									fontSize: 14,
									lineHeight: 1.5,
									color: 'var(--text-primary)',
								}}
							>
								I&apos;m folding what you brought over into what Bridgeway
								already knows about your business.
							</p>
							{showImportFailure && (
								<div
									role="status"
									style={{ fontSize: 14, color: 'var(--text-primary)' }}
								>
									Some of the memories you imported couldn&apos;t be saved.
								</div>
							)}
							<div className="flex items-center" style={{ gap: 20 }}>
								<Button variant="primary" onPress={handleDone}>
									Done
								</Button>
								<button
									type="button"
									onClick={handleRestart}
									style={linkButtonStyle}
								>
									Import from another source
								</button>
							</div>
						</div>
					)}
				</div>
			)}
		</Section>
	);
});
